import React, { useState, useEffect, useRef } from 'react';
import { AutoComplete } from 'antd';
import { useUser } from 'src/contexts/User/User';
import { getIsoCountry } from './utils';
import { PlacesAutocompleteAddon } from './PlacesAutocompleteAddon';
import styled from 'styled-components';

type Suggestion = {
  description: string;
  place_id: string;
};

interface PlacesAutocompleteViewProps {
  id: string;
  placeholder: string | undefined;
  value: string;
  handleSearch: (value: string) => void;
  handleChange: (data: string) => void;
  handleSelect: (address: string) => void;
  places: Suggestion[];
  defaultValue?: string;
  disabled?: boolean;
  selectedCountry: string;
  handleCountryChange: (value: string) => void;
}
interface PlacesAutocompleteProps {
  id: string;
  onSelectChange: (place?: Suggestion | string) => void;
  placeholder?: string;
  defaultValue?: string;
  disabled?: boolean;
}

const { Option } = AutoComplete;

export const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  id,
  onSelectChange,
  placeholder,
  defaultValue,
  disabled,
}) => {
  const { user } = useUser();
  const [selectedCountry, setSelectedCountry] = useState<string>(
    user?.customer?.places[0]?.address.country || 'France'
  );

  const [value, setValue] = useState<string>('');
  const [places, setPlaces] = useState<Suggestion[]>([]);
  const debounceTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    !!defaultValue && setValue(defaultValue);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = window.setTimeout(async () => {
      if (!value || value.trim().length < 3) {
        setPlaces([]);
        return;
      }
      try {
        const apiKey = process.env.REACT_APP_PLACES_API_KEY;
        if (!apiKey) {
          setPlaces([]);
          return;
        }

        const countryCode = getIsoCountry(selectedCountry);
        const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': apiKey,
            'X-Goog-FieldMask': '*',
          },
          body: JSON.stringify({
            input: value,
            includedRegionCodes: [countryCode],
            languageCode: 'fr',
          }),
        });
        if (!response.ok) {
          setPlaces([]);
          return;
        }
        const json = await response.json();
        const suggestions: Suggestion[] = (json?.suggestions || [])
          .map((s: any) => {
            const placePrediction = s.placePrediction;
            if (!placePrediction) return undefined;
            const placeId = placePrediction.placeId;
            const main = placePrediction.structuredFormat?.mainText?.text;
            const secondary = placePrediction.structuredFormat?.secondaryText?.text;
            const fullText = placePrediction.text?.text || [main, secondary].filter(Boolean).join(', ');
            if (!placeId || !fullText) return undefined;
            return {
              description: fullText,
              place_id: placeId,
            } as Suggestion;
          })
          .filter(Boolean);
        setPlaces(suggestions);
      } catch (e) {
        setPlaces([]);
      }
    }, 250);
    return () => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
  }, [value, selectedCountry]);

  const handleChange = (data: string) => {
    setValue(data);
    onSelectChange(data);
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    emptyAddressField();
  };

  const handleSelect = (address: string) => {
    const selectedPlace = places.find(
      (place: Suggestion) => place.description === address
    );
    onSelectChange(selectedPlace);
  };

  const handleSearch = (text: string) => {
    setValue(text);
  };

  const emptyAddressField = () => {
    if (!!defaultValue) {
      setValue('');
      setPlaces([]);
    }
  };

  const props = {
    id,
    defaultValue,
    value,
    placeholder,
    handleSearch,
    handleChange,
    handleCountryChange,
    handleSelect,
    disabled,
    selectedCountry,
  };

  return <PlacesAutocompleteView {...props} places={places} />;
};

export const PlacesAutocompleteView: React.FC<PlacesAutocompleteViewProps> = ({
  id,
  placeholder,
  value,
  handleSearch,
  handleChange,
  handleCountryChange,
  handleSelect,
  places,
  defaultValue,
  disabled,
  selectedCountry,
}) => {
  const children =
    places.length > 0 &&
    places.map((place: Suggestion) => {
      return (
        <Option key={place.place_id} value={place.description}>
          {place.description}
        </Option>
      );
    });

  return (
    <AutocompleteContainer>
      <PlacesAutocompleteAddon
        defaultValue={selectedCountry}
        onChange={handleCountryChange}
        disabled={disabled}
      />
      <AutoComplete
        defaultValue={defaultValue}
        style={{ width: '100%' }}
        size="large"
        onSearch={handleSearch}
        onChange={handleChange}
        onSelect={handleSelect}
        placeholder={placeholder || 'Adresse'}
        id={id || 'autoCompletePlaces'}
        data-testid="autoCompletePlaces"
        disabled={disabled}
        value={value}
      >
        {children}
      </AutoComplete>
    </AutocompleteContainer>
  );
};

const AutocompleteContainer = styled.div`
  display: flex;

  .ant-select {
    &:first-child {
      flex: 0;

      .ant-select-selector {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    }

    + .ant-select {
      .ant-select-selector {
        border-left: 0;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }
  }
`;
